import { useState, useEffect, useMemo } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, GarageObject } from '../services/adminApi'
import styles from './GarageFilesPage.module.css'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function extIcon(key: string): string {
  const ext = key.split('.').pop()?.toLowerCase() ?? ''
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return '🖼'
  if (['glb', 'gltf', 'fbx', 'obj'].includes(ext)) return '📦'
  if (['mp3', 'wav', 'ogg'].includes(ext)) return '🎵'
  if (['json', 'yaml', 'yml'].includes(ext)) return '📄'
  return '📃'
}

interface DirEntry {
  type: 'folder'
  name: string
  fileCount: number
  totalSize: number
}

interface FileEntry {
  type: 'file'
  name: string
  object: GarageObject
}

type Entry = DirEntry | FileEntry

function buildEntries(objects: GarageObject[], path: string[]): Entry[] {
  const prefix = path.length > 0 ? path.join('/') + '/' : ''
  const folders = new Map<string, { count: number; size: number }>()
  const files: FileEntry[] = []

  for (const obj of objects) {
    if (!obj.key.startsWith(prefix)) continue
    const rest = obj.key.slice(prefix.length)
    const slashIdx = rest.indexOf('/')
    if (slashIdx !== -1) {
      const folderName = rest.slice(0, slashIdx)
      const prev = folders.get(folderName) ?? { count: 0, size: 0 }
      folders.set(folderName, { count: prev.count + 1, size: prev.size + obj.size })
    } else {
      files.push({ type: 'file', name: rest, object: obj })
    }
  }

  const folderEntries: DirEntry[] = [...folders.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, { count, size }]) => ({ type: 'folder', name, fileCount: count, totalSize: size }))

  files.sort((a, b) => a.name.localeCompare(b.name))

  return [...folderEntries, ...files]
}

export default function GarageFilesPage() {
  const { apiKey } = useAdmin()
  const [objects, setObjects] = useState<GarageObject[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [path, setPath] = useState<string[]>([])
  const [filter, setFilter] = useState('')

  function load() {
    const api = createAdminApi(() => apiKey)
    setLoading(true)
    setError('')
    api.listGarageObjects()
      .then(setObjects)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [apiKey]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(key: string) {
    if (!confirm(`Delete "${key}"?`)) return
    const api = createAdminApi(() => apiKey)
    try {
      await api.deleteGarageObject(key)
      setObjects(prev => prev.filter(o => o.key !== key))
    } catch (e) {
      alert('Delete failed: ' + (e instanceof Error ? e.message : String(e)))
    }
  }

  const entries = useMemo(() => buildEntries(objects, path), [objects, path])

  const filteredEntries = useMemo(() => {
    if (!filter) return entries
    const q = filter.toLowerCase()
    return entries.filter(e => e.name.toLowerCase().includes(q))
  }, [entries, filter])

  const totalSize = objects.reduce((s, o) => s + o.size, 0)

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Garage S3 Files</h1>
        <div className={styles.headerRight}>
          {!loading && (
            <span className={styles.statPill}>{objects.length} files · {formatBytes(totalSize)}</span>
          )}
          <input
            type="text"
            className={styles.filterInput}
            placeholder="Filter…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <button className={styles.refreshBtn} onClick={load}>Refresh</button>
        </div>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <nav className={styles.breadcrumb}>
        <button
          className={`${styles.crumb} ${path.length === 0 ? styles.crumbActive : ''}`}
          onClick={() => { setPath([]); setFilter('') }}
        >
          bucket
        </button>
        {path.map((segment, i) => (
          <span key={i} className={styles.crumbGroup}>
            <span className={styles.crumbSep}>/</span>
            <button
              className={`${styles.crumb} ${i === path.length - 1 ? styles.crumbActive : ''}`}
              onClick={() => { setPath(path.slice(0, i + 1)); setFilter('') }}
            >
              {segment}
            </button>
          </span>
        ))}
      </nav>

      <div className={styles.content}>
        {loading ? (
          <p className={styles.empty}>Loading…</p>
        ) : filteredEntries.length === 0 ? (
          <p className={styles.empty}>{filter ? 'No entries match that filter.' : 'Empty folder.'}</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Size</th>
                <th>Last Modified</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map(entry => (
                entry.type === 'folder' ? (
                  <tr
                    key={entry.name}
                    className={`${styles.row} ${styles.folderRow}`}
                    onClick={() => { setPath([...path, entry.name]); setFilter('') }}
                  >
                    <td className={styles.nameCell}>
                      <span className={styles.folderIcon}>📁</span>
                      <span className={styles.folderName}>{entry.name}</span>
                    </td>
                    <td className={styles.sizeCell}>{formatBytes(entry.totalSize)}</td>
                    <td className={styles.dateCell}></td>
                    <td className={styles.typeCell}>
                      <span className={styles.folderMeta}>{entry.fileCount} file{entry.fileCount !== 1 ? 's' : ''}</span>
                    </td>
                    <td></td>
                  </tr>
                ) : (
                  <tr key={entry.name} className={styles.row}>
                    <td className={styles.nameCell}>
                      <span className={styles.fileIcon}>{extIcon(entry.name)}</span>
                      <a
                        href={entry.object.url}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.fileLink}
                        title={entry.object.key}
                        onClick={e => e.stopPropagation()}
                      >
                        {entry.name}
                      </a>
                    </td>
                    <td className={styles.sizeCell}>{formatBytes(entry.object.size)}</td>
                    <td className={styles.dateCell}>{formatDate(entry.object.lastModified)}</td>
                    <td className={styles.typeCell}>{entry.object.contentType || '—'}</td>
                    <td>
                      <button
                        className={styles.deleteBtn}
                        onClick={() => handleDelete(entry.object.key)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && path.length > 0 && (
        <div className={styles.footer}>
          <button className={styles.upBtn} onClick={() => { setPath(path.slice(0, -1)); setFilter('') }}>
            ↑ Up
          </button>
        </div>
      )}
    </div>
  )
}
